import { LuCheck, LuFlame } from "react-icons/lu";
import type { DayStreak } from "../store";
import { Coin } from "./ui";

const weekday = (day: string) => new Date(day + "T12:00:00Z").toLocaleDateString("da-DK", { weekday: "long", timeZone: "UTC" });

/**
 * v18: "dage i træk" – a gentle daily streak with a week row. Always positive: after a break it says "Ny start", never "you lost".
 * One missed day is forgiven (a "hviledag"), and a small bonus waits at 3, 7, 14 and 30 days.
 */
export function StreakCard({ s, className = "" }: { s: DayStreak; className?: string }) {
  const n = s.current;
  const left = Math.max(0, s.next.days - n);
  const msg = s.doneToday
    ? "Flot! Du har spillet i dag – kom igen i morgen."
    : s.restDay
      ? "Du holdt en hviledag. Spil i dag, så bliver striben ved!"
      : n > 0
        ? "Spil et spil i dag, så vokser din stribe!"
        : "Start en stribe i dag – spil dit første spil!";
  return (
    <section className={`streakcard${n > 0 ? " alive" : ""}${s.doneToday ? " done" : ""} ${className}`} aria-label={`Dage i træk: ${n}`}>
      <div className="sk-flame" aria-hidden="true">
        <LuFlame size={40} />
        <b>{n}</b>
      </div>
      <div className="sk-main">
        <div className="sk-top"><b>{n === 1 ? "1 dag i træk" : `${n} dage i træk`}</b>{s.best > n && <small>bedst: {s.best}</small>}</div>
        <ol className="sk-week" aria-label="De sidste syv dage">
          {s.week.map((d) => (
            <li key={d.day} className={`${d.played ? "on" : ""}${d.today ? " today" : ""}`} aria-label={`${weekday(d.day)}${d.today ? " (i dag)" : ""}: ${d.played ? "spillet" : "ikke spillet"}`}>
              <span className="sk-dot" aria-hidden="true">{d.played ? <LuCheck size={16} strokeWidth={3.5} /> : null}</span>
              <small aria-hidden="true">{d.label}</small>
            </li>
          ))}
        </ol>
        <p className="sk-msg">{msg}</p>
        <p className="sk-next"><Coin /> Om {left === 1 ? "1 dag" : `${left} dage`}: +{s.next.coins} mønter</p>
      </div>
    </section>
  );
}

/** a line on the result screen: "Dag 4 i træk!" (+ bonus) */
export function StreakNote({ streak }: { streak: { current: number; graceUsed: boolean; bonus: number } }) {
  return (
    <div className="streaknote" role="status">
      <span className="sn-ico"><LuFlame aria-hidden="true" size={26} /></span>
      <span>
        <b>{streak.current === 1 ? "Din stribe er i gang!" : `Dag ${streak.current} i træk!`}</b>
        {streak.graceUsed && <small> Du holdt en hviledag – striben blev reddet.</small>}
        {streak.bonus > 0 && <small> Bonus for din stribe: <Coin /> +{streak.bonus} mønter</small>}
      </span>
    </div>
  );
}
